import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { FormControl, TextField, MenuItem, Button, Grid, Tooltip } from '@material-ui/core';
import { saveAs } from 'file-saver';
import { LoadingCircle, TitleBackButton, ExportCSVButton, PrintButton, UserTypes } from '../../common';
import AtoFFilters from '../AtoFFilters';
import StaleReportNotice from '../Common/StaleReportNotice';
import D1Table from '../D1Table';
import {
  ReportSections,
  ReportSectionsDistrict,
  ReportSectionKeys,
  ReportSectionsForFreeUsers,
  JobNames,
} from '../+store/aToF';
import D2aTable from '../D2aTable';
import D2bTable from '../D2bTable';
import D3 from '../D3';
import D1GradRate from '../D1GradRate';
import D1Ccmr from '../D1Ccmr';
import SectionSummary from '../SectionSummary';
import Summary from '../Summary';
import SummaryDetails from '../SummaryDetails';
import CampusesWeights from '../CampusesWeights';
import StudentsList from '../Common/StudentsList';
import EnrollmentSourceBadge from '../Common/EnrollmentSourceBadge';
import { serializeWithStyles, copyComputedStyle } from './serializeWithStyles';
import exportAsCsv from './exportAsCsv';
import InfoIcon from '@material-ui/icons/Info';

const isDemo = process.env.REACT_APP_THEME === 'demo';

const styles = (theme) => ({
  root: {
    padding: '16px 24px',
    paddingBottom: isDemo ? 48 : 180,
  },
  content: {
    margin: 'auto',
  },
  progress: {},
  textField: {
    minWidth: '280px',
    marginRight: 8,
  },
  formControl: {},
  header: {
    display: 'flex',
    marginBottom: 24,
  },
  subheader: {
    display: 'flex',
    justifyContent: 'center',
  },
});

// const tempData = [
//   {
//     text: 'Hey my name is guy',
//     icon: 'check',
//   },
//   {
//     text: 'Hey my name is BALL',
//     icon: 'times',
//   },
// ];

class AtoFBase extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    loading: PropTypes.bool.isRequired,
    reportStale: PropTypes.bool,
    restoreLastRun: PropTypes.func,
    loadingMsg: PropTypes.string,
    profile: PropTypes.object,
    needsInit: PropTypes.bool.isRequired,
    init: PropTypes.func.isRequired,
    status: PropTypes.shape({
      error: PropTypes.string,
      message: PropTypes.string,
    }).isRequired,
    assessmentList: PropTypes.array.isRequired,
    getSchools: PropTypes.func.isRequired,
    reportData: PropTypes.object,
    getAssessmentGroups: PropTypes.func.isRequired,
    assessmentGroups: PropTypes.object.isRequired,
    getStudentRoster: PropTypes.func,
    students: PropTypes.object,
    avatars: PropTypes.object,
    getAvatars: PropTypes.func,
    handleGetStudentsList: PropTypes.func,
  };

  state = {
    reportSection: ReportSectionKeys.summary,
    showTenths: false,
    // printing: false,
  };

  componentDidMount() {
    this.shouldInit();
  }

  componentDidUpdate() {
    this.shouldInit();
  }

  handleChange = (name) => (event) => {
    switch (name) {
      case 'reportSection':
        this.setState({ reportSection: event.target.value });
        break;
      default:
    }
  };
  getAmount = (amount, showTenths, rawAmount) => {
    // Campus reports store officially-rounded scores; rawAmount carries the
    // pre-rounding value so tenths can be shown. District scores are already
    // fractional, so they have no rawAmount and fall back to amount.
    const value = showTenths && rawAmount != null && !isNaN(rawAmount) ? rawAmount : amount;
    if (value && !isNaN(value)) {
      return showTenths ? value.toFixed(1) : value.toFixed(0);
    }
    return null;
  };
  changeReportSection = (name) => {
    this.setState({ reportSection: name });
  };

  exportCsv = (reportSection) => async () => {
    const { reportData, filters, schoolList } = this.props;
    // Campus groups aren't in the schools store — fall back to the report's
    // own name before the raw "group:{id}" selector.
    const districtCampusName = schoolList[filters.school]
      ? schoolList[filters.school].name
      : reportData.districtOrCampusName || filters.school;
    const { blob, fileName } = await exportAsCsv(reportSection, reportData, districtCampusName);
    if (blob) {
      saveAs(blob, fileName);
    }
  };

  exportPdf = (reportSection) => () => {
    const { filters, schoolList, reportData } = this.props;
    const districtCampusName = schoolList[filters.school]
      ? schoolList[filters.school].name
      : (reportData && reportData.districtOrCampusName) || filters.school;
    const summaryElement = document.getElementById('reportSectionSummary');
    const summaryContent = summaryElement != null ? summaryElement.cloneNode(true) : null;
    const title = document.createElement('H3');
    const titles = {
      [ReportSectionKeys.summary]: { title: 'Summary Report', elementId: 'reportSummary' },
      [ReportSectionKeys.summaryDetails]: { title: 'Summary Report Details', elementId: 'reportSummaryDetails' },
      [ReportSectionKeys.d1staar]: { title: 'Student Achievement: STAAR Performance', elementId: 'reportD1' },
      [ReportSectionKeys.d1ccmr]: {
        title: 'Student Achievement: College, Career and Military Readiness',
        elementId: 'reportCCMR',
      },
      [ReportSectionKeys.d1gradRate]: { title: 'Student Achievement: Graduation Rate', elementId: 'reportGrad' },
      [ReportSectionKeys.d2a]: { title: 'School Progress: Academic Growth', elementId: 'reportD2a' },
      [ReportSectionKeys.d2b]: { title: 'School Progress: Relative Performance', elementId: 'reportD2b' },
      [ReportSectionKeys.d3]: { title: 'Closing Gaps', elementId: 'reportD3' },
      [ReportSectionKeys.campusesWeights]: { title: 'Campuses Weights', elementId: 'reportCampusesWeights' },
    };
    if (titles[reportSection]) {
      const element = document.getElementById(titles[reportSection].elementId);
      const textTitle = `${districtCampusName} - ${titles[reportSection].title}`;
      const content = element.cloneNode(true);
      copyComputedStyle(element, content, true);
      title.append(document.createTextNode(textTitle));
      if (summaryContent) {
        content.prepend(summaryContent);
        copyComputedStyle(document.getElementById('reportSectionSummary').cloneNode(true), summaryContent);
      }
      content.prepend(title);
      const pri = document.getElementById('ifmcontentstoprint').contentWindow;
      pri.document.open();
      let contectHtml = serializeWithStyles(content);
      let headHtml = '';
      if (document.getElementsByTagName('head') && document.getElementsByTagName('head').length > 0) {
        let headTag = document.getElementsByTagName('head')[0];
        let arrHeadStyles = headTag.getElementsByTagName('style');
        if (arrHeadStyles && arrHeadStyles.length > 0) {
          for (let i = 0; i < arrHeadStyles.length; i++) {
            headHtml += arrHeadStyles[i].outerHTML;
          }
        }
      }
      var htmlDoc = '';
      htmlDoc += headHtml;
      htmlDoc += ' ';
      htmlDoc += contectHtml;

      pri.document.write(htmlDoc);
      pri.document.close();
      pri.focus();
      pri.print();
      if (summaryContent) {
        content.removeChild(summaryContent);
      }
      content.removeChild(title);
    }
  };

  shouldInit() {
    const {
      profile,
      needsInit,
      init,
      loading,
      getSchools,
      getAssessmentGroups,
      students,
      getStudentRoster,
      avatars,
      getAvatars,
    } = this.props;
    if (profile && needsInit && !loading) {
      init(profile);
      getSchools(profile);
      getAssessmentGroups(profile);
      if (!Object.keys(students).length) {
        getStudentRoster(profile);
      }
      if (!avatars) {
        getAvatars();
      }
    }
  }
  handleShowTenthsChange(val) {
    this.setState({ showTenths: val });
  }
  render() {
    const {
      classes,
      loading,
      profile,
      assessmentList,
      reportData,
      assessmentGroups,
      loadingMsg,
      handleGetStudentsList,
      reportStale,
      restoreLastRun,
    } = this.props;
    const { reportSection, showTenths } = this.state;
    // Filters were touched after this report ran, so it no longer describes what the filter
    // bar says. Hide it behind a prompt rather than let it be read as current. reportData is
    // left untouched in the store so Cancel restores it without re-running anything.
    const showStaleNotice = !!reportStale && !!reportData;

    // free user type can only see summary section
    let reportSections;
    if (profile) {
      reportSections = profile.userType === UserTypes.free ? ReportSectionsForFreeUsers : ReportSections;
    }
    let reportSectionsDistrict;
    if (profile) {
      reportSectionsDistrict =
        profile.userType === UserTypes.free ? ReportSectionsForFreeUsers : ReportSectionsDistrict;
    }

    // Every report-dependent element on this page hangs off `report`, so blanking it here
    // hides the whole body — table, section picker, export and print — in one move.
    let report = reportData && !showStaleNotice ? reportData.report : null;
    let allCampusReports = reportData && !showStaleNotice ? reportData.allCampusReports : [];
    let provisionsMessage = reportData && !showStaleNotice ? reportData.provisionsMessage : null;

    let sectionComponent = null;
    let sectionComponentCampusInDistrict = null;
    let sectionSummaryComponent = null;
    if (report) {
      switch (reportSection) {
        case ReportSectionKeys.summary:
          if (report.summary) {
            sectionComponent = (
              <div>
                <Grid container direction="row" justify="center" alignItems="center" style={{ padding: '16px 0 24px' }}>
                  <Button
                    variant="contained"
                    style={{ padding: '8px 24px' }}
                    onClick={(e) => this.handleShowTenthsChange(!showTenths)}
                  >
                    {showTenths ? 'Hide Tenths' : 'Show Tenths'}
                  </Button>
                </Grid>
                {report.isDistrict && (
                  <Grid container direction="row" justify="center" alignItems="center">
                    <EnrollmentSourceBadge enrollmentSource={report.enrollmentSource} />
                  </Grid>
                )}
                <Summary
                  report={report}
                  provisionsMessage={provisionsMessage}
                  onChangeReportSection={this.changeReportSection}
                  isFreeAccount={profile.userType === UserTypes.free}
                  showTenths={showTenths}
                />
              </div>
            );
            sectionSummaryComponent = (
              <SectionSummary
                scaled={this.getAmount(report.summary.score, showTenths, report.summary.scoreRaw)}
                letter={report.summary.letter}
              />
            );
          }
          break;
        case ReportSectionKeys.summaryDetails:
          if (report.summary) {
            const totalEnrollment = allCampusReports
              ? allCampusReports.reduce((sum, c) => sum + (c.campus3To12StudentsCount || 0), 0)
              : 0;
            if (report.isDistrict && allCampusReports) {
              // District view: one unified table with sticky header + all campus rows
              sectionComponent = (
                <div id="reportSummaryDetails">
                  <SummaryDetails
                    report={report}
                    districtOrCampusName={reportData.districtOrCampusName}
                    districtOrCampusNumber={reportData.districtOrCampusNumber}
                    hideHeader={false}
                    schoolYear={reportData.schoolYear}
                    totalEnrollment={totalEnrollment}
                    wrapInTable={true}
                    campusRows={allCampusReports}
                    profile={profile}
                  />
                </div>
              );
            } else {
              // Single campus view: standalone table
              sectionComponent = (
                <div>
                  <SummaryDetails
                    report={report}
                    districtOrCampusName={reportData.districtOrCampusName}
                    districtOrCampusNumber={reportData.districtOrCampusNumber}
                    hideHeader={false}
                    schoolYear={reportData.schoolYear}
                  />
                </div>
              );
            }
          }
          break;
        case ReportSectionKeys.d1staar:
          if (report.d1 && report.d1.staar) {
            sectionComponent = <D1Table domain={report.d1.staar} columnTags={report.columnTags} />;
            sectionSummaryComponent = (
              <SectionSummary
                raw={report.d1.staar.raw}
                scaled={report.d1.staar.scaled}
                letter={report.d1.staar.letter}
              />
            );
          }
          break;
        case ReportSectionKeys.d1ccmr:
          if (report.d1 && report.d1.ccmr) {
            sectionComponent = <D1Ccmr domain={report.d1.ccmr} />;
            sectionSummaryComponent = (
              <SectionSummary
                raw={!isNaN(report.d1.ccmr.raw) && parseFloat(report.d1.ccmr.raw).toFixed()}
                scaled={report.d1.ccmr.scaled}
                letter={report.d1.ccmr.letter}
              />
            );
          }
          break;
        case ReportSectionKeys.d1gradRate:
          if (report.d1 && report.d1.gradRate) {
            sectionComponent = <D1GradRate domain={report.d1.gradRate} />;
            sectionSummaryComponent = (
              <SectionSummary
                raw={report.d1.gradRate.raw}
                scaled={report.d1.gradRate.scaled}
                letter={report.d1.gradRate.letter}
              />
            );
          }
          break;
        case ReportSectionKeys.d2a:
          if (report.d2a) {
            sectionComponent = <D2aTable report={report} />;
            sectionSummaryComponent = (
              <SectionSummary raw={report.d2a.raw} scaled={report.d2a.scaled} letter={report.d2a.letter} />
            );
          }
          break;
        case ReportSectionKeys.d2b:
          if (report.d2b) {
            sectionComponent = <D2bTable report={report} />;
            sectionSummaryComponent = (
              <SectionSummary raw={report.d2b.raw} scaled={report.d2b.scaled} letter={report.d2b.letter} />
            );
          }
          break;
        case ReportSectionKeys.d3:
          if (report.d3) {
            sectionComponent = <D3 report={report} profile={profile} handleGetStudentsList={handleGetStudentsList} />;
            sectionSummaryComponent = (
              <SectionSummary raw={report.d3.raw} scaled={report.d3.scaled} letter={report.d3.letter} />
            );
          }
          break;
        case ReportSectionKeys.campusesWeights:
          if (report.d3) {
            sectionComponent = <CampusesWeights report={report} />;
          }
          break;
        default:
      }
    }
    return (
      <div className={classes.root} style={{ background: report ? 'white' : 'inherit' }}>
        {(loading || !profile) && <LoadingCircle classes={classes} msg={loadingMsg} />}
        <StudentsList />
        {/* <div>
          { profile && <AssessmentChecklist data={tempData} />}
        </div> */}
        <div className={classes.header} style={{ display: loading || !profile ? 'none' : 'flex' }}>
          <div style={{ width: 200 }}>
            <TitleBackButton title="A-F Reports" />
            {report && (
              <div>
                <ExportCSVButton title="Export As CSV" onClick={this.exportCsv(reportSection)} />
                <iframe
                  title="print"
                  id="ifmcontentstoprint"
                  style={{
                    height: '0px',
                    width: '0px',
                    position: 'absolute',
                  }}
                />
                <PrintButton title="Print" onClick={this.exportPdf(reportSection)} />
              </div>
            )}
          </div>
          {assessmentList && profile && (
            <div>
              <AtoFFilters
                assessmentGroups={assessmentGroups}
                profile={profile}
                jobName={JobNames.aToF}
                orientation="horizontal"
              />
            </div>
          )}
        </div>
        {showStaleNotice && <StaleReportNotice onCancel={restoreLastRun} />}
        <div className={classes.subheader}>
          {report && (
            <div>
              <FormControl className={classes.formControl}>
                <TextField
                  select
                  label="Report Section"
                  className={classes.textField}
                  value={reportSection}
                  onChange={this.handleChange('reportSection')}
                  SelectProps={{
                    MenuProps: {
                      className: classes.menu,
                    },
                  }}
                  margin="normal"
                  variant="outlined"
                >
                  {Object.entries(report.isDistrict ? reportSectionsDistrict : reportSections).map(([value, label]) => {
                    let reportDataKey = value;
                    if (value == 'summaryDetails') {
                      reportDataKey = 'summary';
                    }
                    if (
                      (report[reportDataKey] &&
                        (reportDataKey == 'campusesWeights' ||
                          report[reportDataKey].letter ||
                          report[reportDataKey].msg)) ||
                      (report.d1[reportDataKey.split('d1')[1]] &&
                        (report.d1[reportDataKey.split('d1')[1]].scaled || report.d1[reportDataKey.split('d1')[1]].msg))
                    ) {
                      let isHidden =
                        (report.ccmrSource == 'manual' || report.ccmrHasStudentData === false) && value == 'd1ccmr';
                      return (
                        !isHidden && (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        )
                      );
                    }
                    return null;
                  })}
                </TextField>
              </FormControl>
            </div>
          )}
        </div>
        {report && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              marginBottom: '30px',
              marginTop: '30px',
            }}
          >
            {sectionSummaryComponent}
          </div>
        )}

        {sectionComponent}
        {sectionComponentCampusInDistrict}
        {isDemo && !report && !loading && profile && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '72px 24px',
            }}
          >
            <InfoIcon style={{ fontSize: 48, color: '#cbd5d8', marginBottom: 12 }} />
            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#5d6b6d' }}>No report to show yet</div>
            <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: 4 }}>
              Choose a district or campus above to load its A-F report.
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withStyles(styles)(AtoFBase);
